// Reusable app-wide error boundary. Ported/generalized from the bespoke
// CalendarErrorBoundary (routes/calendar/index.tsx). Plan: durability/parity.
//
// Why a class: React only surfaces render-time throws to class components via
// getDerivedStateFromError/componentDidCatch. A single render throw anywhere in
// a route subtree white-screens the whole SPA otherwise. Wrapping each top-level
// route with this keeps the shell + nav alive so one crashing surface doesn't
// take down the app.
//
// The fallback keeps the on-screen stack behavior of the old calendar boundary
// (mobile users can't pop devtools; this is how "X is black" gets debugged),
// laid out as a Proton error state rather than a black void. It offers
// "Reload" (hard) and "Try again" (soft reset).

import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/proton/ui/Button";
import { PublicStatusIcon } from "@/proton/ui/Public";

interface ErrorBoundaryProps {
  children: ReactNode;
  // Short slug shown in the fixed corner crash badge (e.g. "app", "calendar").
  // Mirrors the old "cal-CRASHED" marker so a screenshot alone says what broke.
  label?: string;
  // Optional custom fallback. Receives the error + a soft-reset fn. When
  // provided it fully replaces the default panel.
  fallback?: (error: Error, reset: () => void) => ReactNode;
  // Extra work to run on "Try again" (clear caches, refetch, etc.).
  onReset?: () => void;
  // When this value changes, the boundary auto-clears its error. Pass the
  // current route path so navigating away from a crashed surface recovers it
  // without a full reload.
  resetKey?: string | number;
}

interface ErrorBoundaryState {
  error: Error | null;
  info: string;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, info: "" };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ error, info: info.componentStack ?? "" });
    console.error(`[ErrorBoundary${this.props.label ? `:${this.props.label}` : ""}]`, error, info);
  }

  override componentDidUpdate(prev: ErrorBoundaryProps) {
    // Auto-recover when the reset key changes (e.g. route navigation).
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  reset = () => {
    this.props.onReset?.();
    this.setState({ error: null, info: "" });
  };

  override render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    const label = this.props.label;
    return (
      <div className="flex-1 min-h-0 bg-norm overflow-auto p-4 sm:p-8" style={{ position: "relative" }}>
        {label && (
          <div
            style={{
              position: "fixed", top: 4, right: 4, zIndex: 9999,
              background: "#dc2626", color: "#fff", padding: "2px 6px",
              borderRadius: 4, fontSize: 10, fontFamily: "monospace",
            }}
          >
            {label}-CRASHED
          </div>
        )}

        <div className="mx-auto max-w-2xl pt-8 flex flex-col items-center text-center">
          <PublicStatusIcon icon={AlertTriangle} tone="danger" />
          <h1 className="text-2xl text-bold">Something went wrong</h1>
          <p className="mt-2 mb-0 color-weak">
            This part of the app hit an unexpected error. The rest of your workspace is still fine.
          </p>

          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Button color="norm" onClick={this.reset}>Try again</Button>
            <Button onClick={() => window.location.reload()}>Reload</Button>
          </div>

          <details className="mt-8 w-full text-left">
            <summary className="cursor-pointer select-none text-sm color-weak">Error details</summary>
            <pre className="error-details-stack">
              {error.name}: {error.message}
              {error.stack ? `\n\n${error.stack.slice(0, 2000)}` : ""}
              {info ? `\n\n--- component stack ---\n${info.slice(0, 1500)}` : ""}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
